import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ShieldCheck, Clock, Send, SlidersHorizontal, CheckCircle2 } from 'lucide-react';
import trimboxLogo from '../../../../assets/trimbox-logo.svg';
import { APP_STORE_LINK, PLAY_STORE_LINK } from '../../../../constants';
import { openAppStoreLink } from '../../../../utils/appStoreUtils';

// Simple pill badge
const Badge = ({ children }: { children: React.ReactNode }) => (
  <span
    className='inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium bg-white/60 backdrop-blur border-[#4285F4]/20'
    style={{ borderColor: '#4285F4', color: '#4285F4' }}
  >
    <Sparkles className='h-3.5 w-3.5' />
    {children}
  </span>
);

const Feature = ({ icon: Icon, title, desc }: { icon: any; title: string; desc: string }) => (
  <div className='rounded-2xl border border-black/10 p-6 bg-white/70 backdrop-blur shadow-sm'>
    <div className='flex items-center gap-3'>
      <div className='rounded-xl border border-black/10 p-2'>
        <Icon className='h-5 w-5' />
      </div>
      <h3 className='font-semibold text-lg'>{title}</h3>
    </div>
    <p className='mt-3 text-sm text-black/70 leading-relaxed'>{desc}</p>
  </div>
);

const Step = ({ num, title, desc }: { num: string; title: string; desc: string }) => (
  <div className='flex gap-4 items-start'>
    <div className='h-8 w-8 shrink-0 rounded-full bg-black text-white grid place-items-center text-sm font-semibold'>
      {num}
    </div>
    <div>
      <h4 className='font-semibold'>{title}</h4>
      <p className='text-sm text-black/70 mt-1 leading-relaxed'>{desc}</p>
    </div>
  </div>
);

const FAQ = () => (
  <div className='grid md:grid-cols-2 gap-4'>
    {[
      {
        q: 'How does reply drafting match my tone?',
        a: "We fine‑tune prompts on your prior emails (with permission) and apply a lightweight style profile—sentence length, formality, sign‑offs—so drafts sound like you. You can lock tone to 'Concise', 'Warm', or 'Formal'.",
      },
      {
        q: 'Is it safe to connect my email?',
        a: 'Yes. OAuth-based access, read scopes minimized, and data encrypted in transit and at rest. You can revoke access anytime from your provider settings.',
      },
      {
        q: 'Which inboxes are supported?',
        a: 'Gmail and Google Workspace today. Microsoft 365 and Outlook.com next. IMAP for other providers is on the roadmap.',
      },
      {
        q: "What does 'one hour back' mean?",
        a: 'In beta we measured time-to-zero-inbox and reply throughput for 137 users. Median net savings was 64 minutes per workday after week two.',
      },
    ].map(({ q, a }) => (
      <div key={q} className='rounded-xl border border-black/10 p-5 bg-white/70 backdrop-blur'>
        <div className='font-medium'>{q}</div>
        <div className='text-sm text-black/70 mt-2 leading-relaxed'>{a}</div>
      </div>
    ))}
  </div>
);

const LogoWall = () => (
  <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6 opacity-80'>
    {['Google', 'Microsoft', 'Slack', 'Notion', 'HubSpot', 'Salesforce'].map((name) => (
      <div
        key={name}
        className='text-sm text-center border border-black/5 rounded-xl py-3 bg-white/60'
      >
        {name}
      </div>
    ))}
  </div>
);

const StoreButton = ({
  storeType,
  size = 'default',
}: {
  storeType: 'app' | 'play';
  size?: 'small' | 'default';
}) => {
  const isAppStore = storeType === 'app';

  // Use consistent height but adjust width based on image proportions
  // App Store: 180x54, Play Store: 230x69
  const BUTTON_HEIGHT = size === 'small' ? 50 : 75;
  const BUTTON_WIDTH =
    size === 'small'
      ? isAppStore
        ? 110
        : 120 // Small: More balanced
      : isAppStore
      ? 165
      : 185; // Default: More balanced

  const link = isAppStore ? APP_STORE_LINK : PLAY_STORE_LINK;
  const imageSrc = isAppStore
    ? 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us'
    : 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';
  const altText = isAppStore ? 'Download on the App Store' : 'Get it on Google Play';

  return (
    <button
      onClick={() => openAppStoreLink(link)}
      className='inline-flex items-center justify-center border-none bg-transparent cursor-pointer hover:opacity-80 transition-opacity'
      style={{
        width: BUTTON_WIDTH,
        height: BUTTON_HEIGHT,
      }}
    >
      <img
        src={imageSrc}
        alt={altText}
        style={{
          height: '100%',
          width: 'auto',
          objectFit: 'contain',
          transform: isAppStore ? 'none' : 'scale(1.1)',
        }}
      />
    </button>
  );
};

export default function WelcomeFyxer() {
  return (
    <div
      className='min-h-screen bg-gradient-to-b from-white to-gray-50 text-black'
      style={{ width: '100%' }}
    >
      {/* Nav */}
      <header className='sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-black/5'>
        <div className='mx-auto max-w-6xl px-4 py-3 flex items-center justify-between'>
          <div className='flex items-center gap-2 font-semibold'>
            <img src={trimboxLogo} alt='Trimbox' className='h-7 w-7' />
            Trimbox
          </div>
          <nav className='hidden md:flex items-center gap-6 text-sm'>
            <a href='#features' className='hover:opacity-80'>
              Features
            </a>
            <a href='#how' className='hover:opacity-80'>
              How it works
            </a>
            <a href='#faq' className='hover:opacity-80'>
              FAQ
            </a>
          </nav>
          <div className='flex items-center gap-2'>
            {/* <button
              onClick={next}
              className='inline-flex items-center gap-2 rounded-xl text-white px-4 py-2 text-sm shadow-sm hover:opacity-90'
              style={{ backgroundColor: '#4285F4' }}
            >
              Get started <ArrowRight className='h-4 w-4' />
            </button> */}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className='relative'>
        <div className='mx-auto max-w-6xl px-4 pt-16 pb-10 grid lg:grid-cols-2 gap-10 items-center'>
          <div>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className='flex flex-wrap gap-2 items-center'
            >
              <Badge>Get back 1 hour/day</Badge>
              <span className='text-xs text-black/60'>Private • Secure • Human‑like tone</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className='mt-4 text-4xl md:text-5xl font-semibold leading-tight'
            >
              Your AI executive assistant for email
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className='mt-4 text-black/70 max-w-xl'
            >
              Auto‑organize your inbox, prioritize what matters, and send tone‑perfect replies in
              seconds. Less triage, more impact.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className='mt-2 sm:mt-8 flex flex-col sm:flex-row gap-0 sm:gap-4'
            >
              <StoreButton storeType='app' />
              <StoreButton storeType='play' />
            </motion.div>
          </div>

          {/* Mock UI Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className='rounded-3xl border border-black/10 p-4 bg-white/70 backdrop-blur shadow-lg'
          >
            <div className='rounded-2xl border border-black/10 bg-white overflow-hidden'>
              <div className='flex items-center justify-between px-4 py-3 border-b border-black/10'>
                <div className='text-sm font-medium'>Inbox • Today</div>
                <div className='text-xs text-black/60'>AI Drafts Ready</div>
              </div>
              <div className='grid md:grid-cols-2'>
                <div className='p-4 border-r border-black/5'>
                  {[
                    {
                      from: 'CEO • Tuesday Sync',
                      tag: 'Priority',
                      preview: 'Quick update on Q4 roadmap…',
                    },
                    {
                      from: 'Finance • Receipt',
                      tag: 'Auto‑filed',
                      preview: 'Your Stripe payout…',
                    },
                    {
                      from: 'Recruiting • Scheduling',
                      tag: 'AI drafted',
                      preview: 'Thanks for reaching out…',
                    },
                    {
                      from: 'All‑hands notes',
                      tag: 'Summarized',
                      preview: 'Key decisions and owners…',
                    },
                  ].map((m, i) => (
                    <div key={i} className='py-3 px-3 rounded-lg hover:bg-black/5 cursor-default'>
                      <div className='flex items-center justify-between text-sm'>
                        <div className='font-medium'>{m.from}</div>
                        <span className='text-[9px] uppercase tracking-wide rounded-full bg-black/5 px-2 py-0.5 whitespace-nowrap flex-shrink-0'>
                          {m.tag}
                        </span>
                      </div>
                      <div className='text-xs text-black/60 mt-0.5'>{m.preview}</div>
                    </div>
                  ))}
                </div>
                <div className='p-4'>
                  <div className='text-xs text-black/60 mb-2'>AI reply</div>
                  <div className='rounded-xl border border-black/10 p-3 text-sm leading-relaxed bg-white'>
                    Hi Jordan — thanks for the note. I’m aligned on the plan. Let’s move the launch
                    to Wednesday and I’ll share a brief to unblock design by EOD.
                    <div className='mt-3 text-black/60'>— You</div>
                  </div>
                  <div className='mt-3 flex gap-2'>
                    <button className='inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs hover:bg-black/5'>
                      <SlidersHorizontal className='h-3.5 w-3.5' /> Adjust tone
                    </button>
                    <button
                      className='inline-flex items-center gap-2 rounded-lg text-white px-3 py-2 text-xs hover:opacity-90'
                      style={{ backgroundColor: '#4285F4' }}
                    >
                      <Send className='h-3.5 w-3.5' /> Send
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social proof */}
      <section className='mx-auto max-w-6xl px-4 pb-14'>
        <div className='text-xs uppercase tracking-wider text-black/60 mb-4'>
          Works with your tools
        </div>
        <LogoWall />
      </section>

      {/* Features */}
      <section id='features' className='mx-auto max-w-6xl px-4 py-14'>
        <div className='grid md:grid-cols-[1fr,1.2fr] gap-10 items-start'>
          <div>
            <h2 className='text-2xl md:text-3xl font-semibold'>Less triage. More signal.</h2>
            <p className='mt-3 text-black/70 leading-relaxed max-w-md'>
              We auto‑organize newsletters, receipts, travel, invoices and vendor emails into smart
              folders. Priority threads float to the top with one‑tap summaries and suggested
              replies that match your voice.
            </p>
            <ul className='mt-5 space-y-2 text-sm'>
              {[
                'Priority feed highlights what actually needs you',
                'Auto‑filing for newsletters, receipts, travel and finance',
                'One‑tap summaries on long threads',
                'Drafts that match your tone, ready to send',
              ].map((x) => (
                <li key={x} className='flex items-start gap-2'>
                  <CheckCircle2 className='h-4 w-4 mt-0.5' /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div className='grid sm:grid-cols-2 gap-4'>
            <Feature
              icon={Clock}
              title='Save an hour/day'
              desc='Batch triage and priority surfacing cut context switching. Median 64 minutes saved after week two in beta.'
            />
            <Feature
              icon={SlidersHorizontal}
              title='Matches your tone'
              desc='Concise, warm, or formal. Calibrated from your past emails with opt‑in style learning.'
            />
            <Feature
              icon={ShieldCheck}
              title='Enterprise‑grade privacy'
              desc='OAuth, least‑privilege scopes, encryption at rest & in transit. Data never sold; you stay in control.'
            />
            <Feature
              icon={Send}
              title='Fast on mobile & desktop'
              desc='Keyboard first and mobile‑native flows so you can clear the queue anywhere in minutes.'
            />
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className='mx-auto max-w-6xl px-4 py-14'>
        <div className='text-center mb-8'>
          <h2 className='text-2xl md:text-3xl font-semibold'>Trusted by professionals</h2>
          <p className='mt-3 text-black/70'>See what others are saying about Trimbox</p>
        </div>
        <div className='grid md:grid-cols-2 gap-6'>
          {[
            {
              name: 'Ava Patel',
              role: 'VP Operations, Luma Labs',
              avatar:
                'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=240&auto=format&fit=crop',
              quote:
                'I get ~300 emails a day. This AI assistant gave me back a full hour, every day. My inbox is finally strategic again—not just survival.',
            },
            {
              name: 'Marcus Chen',
              role: 'Product Manager, TechFlow',
              avatar:
                'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=240&auto=format&fit=crop',
              quote:
                "The tone matching is incredible. My team can't tell the difference between my actual emails and AI drafts. Game changer for productivity.",
            },
            {
              name: 'Sarah Williams',
              role: 'Marketing Director, GrowthCo',
              avatar:
                'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=240&auto=format&fit=crop',
              quote:
                'From 200+ unread emails to inbox zero in 30 minutes. The auto-filing feature alone saved me hours of manual organization.',
            },
            {
              name: 'David Rodriguez',
              role: 'Sales Director, RevenueFirst',
              avatar:
                'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=240&auto=format&fit=crop',
              quote:
                'Priority surfacing means I never miss important client emails. My response time improved by 60% since using Trimbox.',
            },
          ].map((testimonial, index) => (
            <div
              key={index}
              className='rounded-2xl border border-black/10 p-6 bg-white/70 backdrop-blur shadow-sm'
            >
              <div className='flex items-center gap-3'>
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className='h-10 w-10 rounded-full object-cover'
                />
                <div>
                  <div className='font-semibold'>{testimonial.name}</div>
                  <div className='text-xs text-black/60'>{testimonial.role}</div>
                </div>
              </div>
              <p className='mt-4 text-sm text-black/80 leading-relaxed'>
                &ldquo;{testimonial.quote}&rdquo;
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id='how' className='mx-auto max-w-6xl px-4 py-14'>
        <h2 className='text-2xl md:text-3xl font-semibold'>How it works</h2>
        <div className='mt-6 grid md:grid-cols-3 gap-6'>
          <Step
            num='1'
            title='Connect your inbox'
            desc='Secure OAuth with Gmail/Workspace. No passwords stored.'
          />
          <Step
            num='2'
            title='Choose your tone'
            desc='Pick concise, warm, or formal; or let the assistant learn from samples.'
          />
          <Step
            num='3'
            title='Triage in batches'
            desc='Smart folders, summaries, and ready‑to‑send drafts clear your queue fast.'
          />
        </div>
      </section>

      {/* Trust & Security */}
      <section className='mx-auto max-w-6xl px-4 py-14'>
        <div className='grid md:grid-cols-2 gap-10 items-start'>
          <div>
            <h2 className='text-2xl md:text-3xl font-semibold'>Built for trust</h2>
            <p className='mt-3 text-black/70 leading-relaxed max-w-md'>
              We treat your inbox like a vault. Your messages never train models shared with other
              customers. SOC 2 in progress. Data residency options for EU/US coming soon.
            </p>
          </div>
          <div className='grid sm:grid-cols-2 gap-4'>
            <Feature
              icon={ShieldCheck}
              title='OAuth only'
              desc='No passwords stored. Revoke access anytime from your provider.'
            />
            <Feature
              icon={ShieldCheck}
              title='Encryption'
              desc='TLS in transit, AES‑256 at rest. Key management with rotation.'
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id='faq' className='mx-auto max-w-6xl px-4 py-14'>
        <div className='text-center mb-8'>
          <h2 className='text-2xl md:text-3xl font-semibold'>Frequently Asked Questions</h2>
          <p className='mt-3 text-black/70'>Everything you need to know about Trimbox</p>
        </div>
        <FAQ />
      </section>

      {/* CTA */}
      <section className='mx-auto max-w-6xl px-4 py-14'>
        <div className='rounded-3xl border border-black/10 p-8 bg-white/80 backdrop-blur text-center'>
          <div className='flex justify-center mb-3'>
            <Badge>Stop living in your inbox</Badge>
          </div>
          <h3 className='text-2xl md:text-3xl font-semibold'>Ready to get started?</h3>
          <p className='mt-2 text-black/70'>
            Connect your inbox and start saving an hour every day.
          </p>
          <div className='mt-5 flex flex-col sm:flex-row justify-center gap-0 sm:gap-4'>
            <StoreButton storeType='app' />
            <StoreButton storeType='play' />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='border-t border-black/5'>
        <div className='mx-auto max-w-6xl px-4 py-10 flex items-center justify-between text-sm'>
          <div className='flex items-center gap-2 font-semibold'>
            <img src={trimboxLogo} alt='Trimbox' className='h-7 w-7' />
            Trimbox
          </div>
          <div className='flex items-center gap-6 text-black/60'>
            <a href='https://trimbox.com/support' className='hover:opacity-80'>
              Support
            </a>
            <a href='https://trimbox.com/privacy' className='hover:opacity-80'>
              Privacy
            </a>
            <a href='https://trimbox.com/terms' className='hover:opacity-80'>
              Terms
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
